import { useEffect, useRef, useState } from "react";
import { RichTextEditorFieldValue } from "../../Fields/types/fieldTypes";
import { DialogProps } from "../../Dialogs/Dialog/Dialog";
import { EmailCommunicationProps } from "../types";
import TextField, {
  TextFieldProps,
} from "../../Fields/BaseInputFields/TextField/TextField";
import {
  FileDocument,
  FileField,
  FileFieldProps,
  RichTextEditorField,
  RichTextEditorFieldProps,
  RichTextEditorValueType,
} from "../../Fields";
import { Footer, dialogStore } from "../../Dialogs";
import { PrimaryButton } from "../../Buttons";
import { SecondaryButton } from "../../Buttons";

const commonTextFieldProps: TextFieldProps = {
  type: "Text",
  value: "",
  label: "",
};

const defaultDialogProps: DialogProps = {
  showCloseIcon: true,
  header: "New Message",
  position: { X: "right", Y: "bottom" },
  allowDragging: false,
  closeOnEscape: false,
  id: "email-comm-component",
};

const defaultRTEProps: RichTextEditorFieldProps = {
  type: "RichTextEditor",
  value: "",
  valueType: RichTextEditorValueType.html,
  label: "",
  richTextEditorSettings: {
    height: "400px",
    toolbarSettings: {
      enableFloating: false,
    },
  },
};

const defaultFileFieldProps: FileFieldProps = {
  type: "File",
  value: undefined,
  label: "Upload Attachments",
  uploaderConfig: {
    multiple: true,
    autoUpload: true,
    directoryUpload: false,
    sequentialUpload: false,
    asyncSettings: {
      chunkSize: 102400,
    },
  },
};

const DEFAULT_EMAIL_DIALOG_HEIGHT = 600; // matching height with default Gmail's dialog height

const EmailCommunicationComponent = ({
  children,
  onSubmit,
  rteConfig,
  fileFieldConfig,
  dialogConfig,
}: EmailCommunicationProps) => {
  const [emailContent, setEmailContent] = useState<string | undefined>();
  const emailContentRef = useRef(emailContent);
  const [emailAttachments] = useState<FileDocument[]>([]);
  const emailAttachmentsRef = useRef(emailAttachments);

  useEffect(() => {
    emailContentRef.current = emailContent;
    emailAttachmentsRef.current = emailAttachments;
  }, [emailContent, emailAttachments]);

  // RTE handling
  const onRTESubmit = (value: RichTextEditorFieldValue) => {
    setEmailContent(value);
  };
  const mergedRTEConfig: RichTextEditorFieldProps = {
    ...defaultRTEProps,
    ...rteConfig,
  };

  // FileField handling
  const mergedFFConfig: FileFieldProps = {
    ...defaultFileFieldProps,
    ...fileFieldConfig,
  };
  const onCompleteUploading = async (doc: FileDocument) => {
    console.log("docs", doc);
    // upload document, capture id, store in emailAttachments and return to developer onSubmit.
  };

  // Dialog handling
  const mergedDialogConfig: DialogProps = {
    ...defaultDialogProps,
    ...dialogConfig,
  };
  const onEmailSubmit = () => {
    onSubmit({
      emailContent: emailContentRef.current,
      emailAttachments: emailAttachmentsRef.current,
    });
  };

  const openEmailDialog = (): void => {
    dialogStore.openDialog({
      content: () => (
        <div
          style={{ minHeight: DEFAULT_EMAIL_DIALOG_HEIGHT }}
          className="ucl-communications-email"
        >
          <TextField
            {...commonTextFieldProps}
            label="From:"
            placeholder="joe.smith@relativitytech.com"
          />
          <TextField {...commonTextFieldProps} label="To:" />
          <hr />
          <TextField {...commonTextFieldProps} label="Subject:" />
          <RichTextEditorField {...mergedRTEConfig} onSubmit={onRTESubmit} />
          <FileField
            {...mergedFFConfig}
            onCompleteUploading={onCompleteUploading}
          />
          <Footer>
            <SecondaryButton
              text="Discard Draft"
              onClick={() =>
                dialogStore.closeDialog(
                  dialogConfig?.id || defaultDialogProps.id
                )
              }
            />
            <PrimaryButton
              cssClass="e-primary"
              text="Send"
              onClick={() => onEmailSubmit()}
            />
          </Footer>
        </div>
      ),
      ...mergedDialogConfig,
    });
  };

  return children(openEmailDialog);
};

export default EmailCommunicationComponent;
